import { queryOptions, useMutation } from '@tanstack/react-query'

import { AccountResponse } from '@forto-dev/customer-master-data-models'

import Client from '@/api/client'
import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { getConfig } from '@/app/config'
import { getUserDetailsFromCurrentToken, isCurrentTokenValid } from '@/utils/auth'
import { removeItem } from '@/utils/storage'

import { InviteeData, InviteeDataSchema } from '../domain/user'

const userServicePath = '/api/user-service/api'

const client = new Client(getConfig().API_ENDPOINT ?? '')

export type LoginProps = {
  email: string
  password: string
}

export type AuthenticateResponse = {
  token: string
}

export const login = async (props: LoginProps): Promise<AuthenticateResponse> => {
  return await client.post<AuthenticateResponse>(`${userServicePath}/legacy/authenticate`, props)
}

export const logout = async (): Promise<null> => {
  removeItem('token')
  return null
}

export const getCustomerAccountData = async (): Promise<AccountResponse | null> => {
  if (!isCurrentTokenValid()) {
    return null
  }
  const accountId = getUserDetailsFromCurrentToken()?.customerId

  if (!accountId) {
    return null
  }

  return await client.get<AccountResponse>(`/api/customer-master-data/accounts/${accountId}`)
}

export type RequestResetPasswordProps = {
  email: string
}

export const requestResetPassword = async (props: RequestResetPasswordProps): Promise<void> =>
  client.post<void>(`${userServicePath}/ship-users/request-password-reset`, {
    ...props,
    redirectTo: window.location.origin,
  })

export type ResetPasswordProps = {
  digest: string
  password: string
}

export const resetPassword = async ({ digest, password }: ResetPasswordProps): Promise<void> =>
  client.post<void>(`${userServicePath}/ship-users/reset-password`, {
    digest,
    password,
  })

export const signupAPIConfig: ApiConfig<{
  acceptInvitation: EndpointConfig<
    {
      digest: string
      customerId: string
      id: string
      password: string
    },
    void
  >
  getInviteeData: EndpointConfig<
    {
      customerId: string
      userId: string
      digest: string
    },
    InviteeData
  >
}> = {
  acceptInvitation: {
    requestConfig: (props) => ({
      endpoint: `${userServicePath}/ship-users/accept-invite`,
      method: 'post',
      body: props,
    }),
  },
  getInviteeData: {
    requestConfig: (props) => ({
      endpoint: `${userServicePath}/ship-users/invitee`,
      method: 'get',
      options: { params: props },
      deserializationStrategy: InviteeDataSchema,
    }),
  },
}

export const signupClient = clientFactory(signupAPIConfig)

export const useAcceptInvitation = () => {
  return useMutation({
    mutationFn: signupClient.acceptInvitation,
  })
}

export const getInviteeDataQueryOptions = ({
  customerId,
  userId,
  digest,
}: {
  customerId: string
  userId: string
  digest: string
}) =>
  queryOptions({
    queryKey: ['inviteeData', customerId, userId, digest],
    queryFn: () =>
      signupClient.getInviteeData({
        customerId,
        userId,
        digest,
      }),
  })
